import { Bounds, MpxSearchQuery, array2dFromBounds, unionOverlapingFeatures } from '@cibo/core'
import { useCallback, useContext, useEffect } from 'react'
//@ts-ignore soon: https://deck.gl/docs/get-started/using-with-typescript
import { useLandManagement } from '@cibo/landmanager'
import { DeckGL } from '@deck.gl/react'
import { Box, Paper, ThemeProvider, useTheme } from '@mui/material'
import {
  ModifyMode,
  RotateMode,
  ScaleMode,
  SplitPolygonMode,
  TranslateMode,
  ViewMode,
} from '@nebula.gl/edit-modes'
import { EditableGeoJsonLayer } from '@nebula.gl/layers'
import { Feature, featureCollection } from '@turf/turf'
import { useHotkeys } from 'react-hotkeys-hook'
import { ViewState } from 'react-map-gl'
import { Map } from 'react-map-gl/dist/es5'
import { LayerContext, LayerRenderer, MapStyles } from '../Layers'
import { useViewportContext } from '../ViewportContext'
import { MAPBOX_STYLES, MAPBOX_TOKEN, themeForMapStyle } from '../const'
import { CustomModifyMode } from './CustomModifyMode'
import { getFeatureFillColor, getFeatureLineColor, getSelectSubLayers } from './EditorStyles'
import { useGeometryEditorStateContext } from './GeometryEditorContext/GeometryEditorStateContext'
import { GeometryEditorToolsContext } from './GeometryEditorContext/GeometryEditorToolsContext'
import { GEOMETRY_SNAPSHOTS, GeoJSONLayerState, GeometryEditorProps } from './GeometryEditorTypes'
import { LayerControls } from './LayerControls'
import { NavigationControl } from './NavigationControl'
import { OtherFieldsLayer } from './OtherFieldsLayer'
import { TransModifyMode } from './ToolGroups'
import { fitBoundsOfBbox, fitBoundsOfFeature } from './geometryEditorUtils'

const DISABLE_MAPS = import.meta.env.VITE_DISABLE_MAPS

export const GeometryEditor = (props: GeometryEditorProps) => (
  <BaseGeometryEditor
    {...props}
    Layers={
      <>
        <LayerRenderer county township parcel state />
        <OtherFieldsLayer />
      </>
    }
  />
)

export const BaseGeometryEditor = ({
  children,
  disabled,
  onChange,
  Layers,
  LayerOverlay,
  displayTools,
  onBack,
  onNext,
  style,
}: GeometryEditorProps) => {
  const theme = useTheme()
  const { mapStyle = MapStyles.SATELLITE } = useContext(LayerContext)
  const { viewport: contextViewport } = useViewportContext()
  const {
    setCopyBuffer,
    copyBuffer,
    mode,
    setMode,
    nextMode,
    setNextMode,
    marqueeFeatures,
    setMarqueeFeatures,
    handleCopy,
    handlePaste,
    handleImmediateDifference,
    handleDifference,
    handleDeleteSelected,
    handleRecenterMap,
    handleUndo,
    handleRedo,
    handleZoomIn,
    handleZoomOut,
    handleViewStateChange,
  } = useContext(GeometryEditorToolsContext)
  const { editMapViewState, setEditMapViewState } = useLandManagement()

  // @todo limit this to some "reasonable" number to avoid memory issues
  const {
    baseFeatureCollection: initialFeatures,
    container,
    editState,
    features,
    setEditState,
    touched,
    setViewport,
    previewFeatures,
    setPreviewFeatures,
  } = useGeometryEditorStateContext()

  /**
   * fit the initial features to the bounds of the initial features (if specified)
   * _after_ we know the height & width of the container
   */
  useEffect(() => {
    if (!container.current || !initialFeatures) return

    if (initialFeatures?.features?.length > 0) {
      setEditMapViewState({
        ...fitBoundsOfFeature({
          features: initialFeatures,
          rect: container.current.getBoundingClientRect(),
          //@ts-ignore
          viewState: editMapViewState,
        }),
      })
    }
  }, [container.current, initialFeatures])

  useEffect(() => {
    if (onChange) {
      onChange({
        features: editState.editLayer.features,
        touched: editState.undoStack.length > 0,
      })
    }
  }, [onChange, editState.undoStack.length, editState.editLayer.features])

  /**
   * reset edit states on initialFeatures change
   */
  useEffect(() => {
    if (!initialFeatures) return

    setEditState({
      editLayer: {
        features: initialFeatures,
        selectedIndexes: initialFeatures?.features?.length > 0 ? [0] : ([] as number[]),
      },
      redoStack: [],
      undoStack: [],
      transforming: false,
    })
    setCopyBuffer(undefined)
    setPreviewFeatures(initialFeatures)
    setMarqueeFeatures(featureCollection([]))
  }, [initialFeatures])

  useHotkeys('cmd+z, ctrl+z', handleUndo, [editState])
  useHotkeys('cmd+shift+z, ctrl+shift+z', handleRedo, [editState])
  useHotkeys('cmd+c, ctrl+c', handleCopy, [editState, marqueeFeatures])
  useHotkeys('cmd+v, ctrl+v', handlePaste, [editState, copyBuffer])
  useHotkeys('delete, backspace', mode.isMarquee ? handleDifference : handleDeleteSelected, [
    previewFeatures,
    marqueeFeatures,
    mode.isMarquee,
    editState.editLayer.selectedIndexes,
  ])

  //@ts-ignore
  const featureLayer = new EditableGeoJsonLayer({
    id: 'geojson-layer',
    data: previewFeatures,
    mode: mode.editLayer.mode,
    modeConfig: mode.editLayer.modeConfig,
    pickable: !disabled,
    selectedFeatureIndexes: mode.isMarquee ? [] : editState.editLayer.selectedIndexes,
    editHandlePointRadiusScale: 1,
    editHandlePointStrokeWidth: 3,
    getEditHandlePointColor: (handle: Feature) => {
      switch (handle.properties?.editHandleType) {
        case 'existing':
          return [0x0, 0x0, 0x0, 0xff]
        case 'intermediate':
        default:
          return [0x0, 0x0, 0x0, 0x80]
      }
    },
    getLineColor: (feature: Feature, isSelected: boolean) =>
      getFeatureLineColor(mapStyle, isSelected),
    getFillColor: getFeatureFillColor(mapStyle, false),
    //@ts-ignore
    onEdit: ({ updatedData, editType }) => {
      if (GEOMETRY_SNAPSHOTS.includes(editType)) {
        const flattenedFeatures = unionOverlapingFeatures(updatedData)
        const editLayer = {
          features: flattenedFeatures,
          selectedIndexes:
            editType === 'addFeature'
              ? [flattenedFeatures.features.length - 1]
              : editState.editLayer.selectedIndexes,
        } as GeoJSONLayerState
        setEditState({
          ...editState,
          editLayer,
          undoStack: [editState.editLayer, ...editState.undoStack],
          redoStack: [],
        })
        setPreviewFeatures(flattenedFeatures)
      } else {
        setPreviewFeatures(updatedData)
      }
    },
  })

  //@ts-ignore
  const marqueeLayer = new EditableGeoJsonLayer({
    id: 'select-layer',
    data: mode.isMarquee && marqueeFeatures,
    mode: mode.marqueeLayer.mode,
    modeConfig: mode.marqueeLayer.modeConfig,
    selectedFeatureIndexes: !!marqueeFeatures?.features.length ? [0] : [],
    pointRadiusMinPixels: 2,
    getFillColor: [255, 255, 255, 0],
    getTentativeFillColor: [255, 255, 255, 0],
    _subLayerProps: getSelectSubLayers(mapStyle),
    //@ts-ignore
    onEdit: ({ updatedData, editType }) => {
      //only allow one shape at a time
      if (editType === 'addFeature') {
        handleImmediateDifference(updatedData)
      } else {
        setMarqueeFeatures(
          mode.marqueeLayer.mode === TranslateMode ? updatedData : featureCollection([])
        )
      }
    },
  })

  const handleLocationSelected = useCallback(
    (query: MpxSearchQuery) => {
      if (!container.current || !editMapViewState) {
        return
      }
      if (!query.lowerLeft || !query.upperRight) {
        return
      }
      const newViewState = fitBoundsOfBbox({
        boundingBox: array2dFromBounds(query as Bounds),
        rect: container.current.getBoundingClientRect(),
        viewState: editMapViewState,
      })

      setEditMapViewState({ ...newViewState })
    },
    [container.current, setEditMapViewState, editMapViewState]
  )

  if (!editMapViewState) return null

  return (
    <div
      style={{ flex: 1, position: 'relative', display: 'flex', overflow: 'hidden', ...style }}
      onContextMenu={evt => evt.preventDefault()}
      ref={container}
    >
      <ThemeProvider theme={themeForMapStyle(theme, mapStyle)}>
        {!!initialFeatures && (
          <DeckGL
            controller={{
              doubleClickZoom: false,
              inertia: mode.isMarquee
                ? mode.marqueeLayer.mode === ViewMode
                : mode.editLayer.mode === ViewMode,
            }}
            pickingRadius={20}
            layers={mode.isMarquee ? [featureLayer, marqueeLayer] : [featureLayer]}
            getCursor={
              mode.isMarquee
                ? marqueeLayer.getCursor.bind(marqueeLayer)
                : featureLayer.getCursor.bind(featureLayer)
            }
            initialViewState={editMapViewState}
            onViewStateChange={handleViewStateChange}
            //@ts-ignore @todo figure out correct type on event
            onClick={(
              info: { index: number; featureType?: string },
              event: { srcEvent: { shiftKey: boolean } }
            ) => {
              if (
                mode.isMarquee &&
                mode.marqueeLayer.mode === TranslateMode &&
                //@ts-ignore
                (!info.layer || info.layer.id !== 'select-layer')
              ) {
                !!nextMode && setMode(nextMode)
                setNextMode(undefined)
                setMarqueeFeatures(featureCollection([]))
              } else if (
                info.index >= 0 &&
                info?.featureType === 'polygons' &&
                [
                  SplitPolygonMode,
                  TranslateMode,
                  TransModifyMode,
                  RotateMode,
                  ScaleMode,
                  CustomModifyMode,
                  ModifyMode,
                  ViewMode,
                ].includes(mode.editLayer.mode)
              ) {
                let selectedIndexes = [info.index]
                if (event.srcEvent.shiftKey) {
                  if (editState.editLayer.selectedIndexes.includes(info.index)) {
                    selectedIndexes = editState.editLayer.selectedIndexes.filter(
                      i => i !== info.index
                    )
                  } else {
                    selectedIndexes = [...editState.editLayer.selectedIndexes, info.index]
                  }
                }
                setEditState({
                  ...editState,
                  editLayer: {
                    ...editState.editLayer,
                    selectedIndexes,
                  },
                })
                //@ts-ignore modeConfig type
              } else if (info.index === -1 && !mode.modeConfig?.booleanOperation) {
                setEditState({
                  ...editState,
                  editLayer: {
                    features: editState.editLayer.features,
                    selectedIndexes: [],
                  },
                })
              }
            }}
          >
            {({ viewState }: { viewState: ViewState }) => {
              !disabled && setViewport(viewState)
            }}
            <Map
              id="mapEditor"
              mapStyle={
                (mapStyle && MAPBOX_STYLES[mapStyle][theme.palette.mode]) ||
                MAPBOX_STYLES.SATELLITE[theme.palette.mode]
              }
              mapboxAccessToken={!DISABLE_MAPS && MAPBOX_TOKEN}
              testMode={DISABLE_MAPS}
              reuseMaps
              {...contextViewport}
              onMove={({ viewState }) => setViewport(viewState)}
            >
              {!!Layers && Layers}
            </Map>
          </DeckGL>
        )}
      </ThemeProvider>

      <LayerControls
        container={container}
        displayTools={displayTools}
        onBack={onBack}
        onNext={onNext}
        onLocationSelected={handleLocationSelected}
        LayerOverlay={LayerOverlay}
      >
        {!!children &&
          typeof children === 'function' &&
          children({
            features,
            touched,
          })}
        <Box display="inline-block">
          <Paper>
            <NavigationControl
              onZoomIn={handleZoomIn}
              onZoomOut={handleZoomOut}
              onRecenter={handleRecenterMap}
            />
          </Paper>
        </Box>
      </LayerControls>
    </div>
  )
}
